import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../../../components/ui/Button";
import Icon from "../../../components/AppIcon";
import { authenticatedFetch } from "../../../auth/auth";

function apiBase() {
  return (import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || "").replace(/\/$/, "");
}

function StatItem({ label, value }) {
  return (
    <div className="rounded-lg border border-border bg-card px-3 py-3">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 text-base font-heading-semibold text-card-foreground">{value}</div>
    </div>
  );
}

export default function BillingAutomationSummaryCard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [config, setConfig] = useState(null);
  const [history, setHistory] = useState([]);

  async function loadSummary() {
    setLoading(true);
    setError("");

    try {
      const [cfgRes, histRes] = await Promise.all([
        authenticatedFetch(`${apiBase()}/api/email/config`, { cache: "no-store" }),
        authenticatedFetch(`${apiBase()}/api/email/history?limit=20&channel=all`, { cache: "no-store" }),
      ]);

      const cfg = await cfgRes.json();
      const hist = await histRes.json();

      if (!cfg?.ok) throw new Error(cfg?.message || "Falha ao carregar campanhas.");
      if (!hist?.ok) throw new Error(hist?.message || "Falha ao carregar histórico.");

      setConfig(cfg);
      setHistory(Array.isArray(hist.items) ? hist.items : []);
    } catch (e) {
      setError(e?.message || "Falha ao carregar resumo da automação.");
      setConfig(null);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadSummary();
  }, []);

  const summary = useMemo(() => {
    const emailRules = Array.isArray(config?.rules) ? config.rules : [];
    const chatcenterRules = Array.isArray(config?.chatcenterRules) ? config.chatcenterRules : [];

    const activeEmailRules = emailRules.filter((item) => item?.enabled).length;
    const activeChatcenterRules = chatcenterRules.filter((item) => item?.enabled).length;

    const emailSentToday = history.filter((item) => {
      if ((item?.channel || "email") !== "email") return false;
      if (item?.status !== "sent") return false;
      const stamp = String(item?.sentAt || item?.createdAt || "");
      return stamp.slice(0, 10) === new Date().toISOString().slice(0, 10);
    }).length;

    const whatsappSentToday = history.filter((item) => {
      if ((item?.channel || "email") !== "chatcenter") return false;
      if (item?.status !== "sent") return false;
      const stamp = String(item?.sentAt || item?.createdAt || "");
      return stamp.slice(0, 10) === new Date().toISOString().slice(0, 10);
    }).length;

    const lastSent = history
      .filter((item) => item?.status === "sent")
      .sort((a, b) => String(b?.sentAt || b?.createdAt || "").localeCompare(String(a?.sentAt || a?.createdAt || "")))[0];

    return {
      activeEmailRules,
      activeChatcenterRules,
      emailSentToday,
      whatsappSentToday,
      lastSentAt: lastSent?.sentAt || lastSent?.createdAt || "-",
    };
  }, [config, history]);

  return (
    <div className="bg-card rounded-lg border border-border p-4 card-shadow h-full">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="font-heading-semibold text-base text-card-foreground">Cobranças Automáticas</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Central separada para regras, templates, histórico e testes de envio.
          </p>
        </div>

        <span className="text-[11px] px-2 py-1 rounded-full bg-primary/10 text-primary border border-primary/20 whitespace-nowrap">
          Área dedicada
        </span>
      </div>

      {error ? (
        <div className="rounded-lg border border-border bg-accent/30 p-3 text-sm text-muted-foreground">
          {error}
        </div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <StatItem label="Regras ativas" value={loading ? "..." : String(summary.activeEmailRules + summary.activeChatcenterRules)} />
            <StatItem label="Último envio" value={loading ? "..." : summary.lastSentAt === "-" ? "-" : String(summary.lastSentAt).replace("T", " ").slice(0, 16)} />
            <StatItem label="E-mails hoje" value={loading ? "..." : String(summary.emailSentToday)} />
            <StatItem label="WhatsApp hoje" value={loading ? "..." : String(summary.whatsappSentToday)} />
          </div>

          <div className="rounded-lg border border-border bg-background p-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">E-mail</span>
              <span className="text-card-foreground">{loading ? "..." : `${summary.activeEmailRules} regra(s) ativa(s)`}</span>
            </div>
            <div className="flex items-center justify-between text-sm mt-2">
              <span className="text-muted-foreground">WhatsApp via ChatCenter</span>
              <span className="text-card-foreground">{loading ? "..." : `${summary.activeChatcenterRules} regra(s) ativa(s)`}</span>
            </div>
          </div>
        </div>
      )}

      <div className="mt-4 flex flex-col sm:flex-row gap-2">
        <Button variant="default" size="sm" className="w-full sm:flex-1" onClick={() => navigate("/billing-automation-dashboard")}>
          <Icon name="Send" size={16} />
          <span className="ml-2">Abrir automação</span>
        </Button>

        <Button variant="outline" size="sm" className="w-full sm:w-auto" onClick={loadSummary} disabled={loading}>
          <Icon name="RefreshCw" size={16} className={loading ? "animate-spin" : ""} />
          <span className="ml-2">Atualizar</span>
        </Button>
      </div>
    </div>
  );
}
